// @ts-strict
import { modelOptions, mongoose, post, prop, type Ref } from "@typegoose/typegoose";
import { z } from "zod";
import { getZodSchemaFromClass, ObjectIdString, validate } from "../../zod-decorator/index.js";
import { AreaOfInterest } from "../areaOfInterest/areaOfInterest.js";
import { VertexInfo } from "../commonSubSchemas/vertexInfo.js";
import type { Organization } from "../organization/organization.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type { NdviImageFieldsWithDefaults, NdviImageSchemaTypes } from "../types/ndviImage.d.js";
import type { DtoValidatorTypes } from "../validatableClass.js";

const SCHEMA_KEY = "NdviImage" as const;

@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: "ndviImages",
        timestamps: true,
        _id: true,
    },
})
@post<SchemaBase<NdviImage>>(...addSchemaKeyHook(SCHEMA_KEY))
export class NdviImage extends SchemaBase<NdviImage, NdviImageFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public createdAt?: Date;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public updatedAt?: Date;

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public name?: string;

    @validate({ zodValidator: z.coerce.date() })
    @prop({ type: () => Date, required: true })
    public date!: Date;

    @validate({
        childClass: VertexInfo,
    })
    @prop({ type: () => VertexInfo, required: true })
    public topLeft!: VertexInfo;

    @validate({
        childClass: VertexInfo,
    })
    @prop({ type: () => VertexInfo, required: true })
    public botRight!: VertexInfo;

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public img_url?: string;

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public gray_url?: string;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    @prop({ type: () => mongoose.Types.ObjectId, ref: "Organization", required: true })
    public owner!: Ref<Organization>;

    @validate({ zodValidator: ObjectIdString })
    @prop({ type: () => mongoose.Types.ObjectId, ref: "AreaOfInterest", required: true })
    public aoi!: Ref<AreaOfInterest>;

    static getters = {} as const satisfies Record<string, (self: NdviImage) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<NdviImageSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<NdviImage, NdviImageFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

NdviImage.dtoValidators = getZodSchemaFromClass(NdviImage) as unknown as DtoValidatorTypes<NdviImageSchemaTypes>;
